import {
  type ComponentProps,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {useTranslation} from 'react-i18next';
import {Button} from '../button';
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from '../collapsible';
import {cn} from '../lib/cn';

const DEFAULT_HEIGHT_LIMIT = 120;

export type ExpandableContentTrigger = ReactElement | ((isOpen: boolean) => ReactElement);

export type ExpandableContentProps = Omit<
  ComponentProps<typeof Collapsible>,
  'children' | 'defaultOpen' | 'onOpenChange' | 'open'
> & {
  children: ReactNode;
  contentClassName?: string;
  defaultOpen?: boolean;
  heightLimit?: number;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  showLessLabel?: ReactNode;
  showMoreLabel?: ReactNode;
  trigger?: ExpandableContentTrigger;
  triggerClassName?: string;
};

export function ExpandableContent({
  children,
  className,
  contentClassName,
  defaultOpen = false,
  disabled,
  heightLimit = DEFAULT_HEIGHT_LIMIT,
  onOpenChange,
  open,
  showLessLabel,
  showMoreLabel,
  trigger,
  triggerClassName,
  ...props
}: ExpandableContentProps) {
  const {t} = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isOpen = open ?? internalOpen;
  const isOverflowing = contentHeight > heightLimit;

  const measureContent = useCallback(() => {
    const nextHeight = contentRef.current?.getBoundingClientRect().height ?? 0;

    setContentHeight(nextHeight);
  }, []);

  useEffect(() => {
    measureContent();
  }, [children, measureContent]);

  useEffect(() => {
    const content = contentRef.current;

    if (!content) {
      return;
    }

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measureContent);

      return () => window.removeEventListener('resize', measureContent);
    }

    const resizeObserver = new ResizeObserver(measureContent);

    resizeObserver.observe(content);

    return () => resizeObserver.disconnect();
  }, [measureContent]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (open === undefined) {
      setInternalOpen(nextOpen);
    }

    onOpenChange?.(nextOpen);
  };

  const expandedHeight = contentHeight ? contentHeight + 1 : undefined;
  const defaultTrigger = (
    <Button variant="link" disabled={disabled} className={cn('mt-2', triggerClassName)}>
      {isOpen ? (showLessLabel ?? t('show_less')) : (showMoreLabel ?? t('show_more'))}
    </Button>
  );
  const renderedTrigger =
    typeof trigger === 'function' ? trigger(isOpen) : (trigger ?? defaultTrigger);

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={handleOpenChange}
      disabled={disabled}
      className={cn('flex flex-col items-start', className)}
      {...props}
    >
      <CollapsibleContent
        forceMount
        className={cn(
          'w-full overflow-hidden transition-[max-height] duration-200 ease-out',
          contentClassName,
        )}
        style={{maxHeight: isOpen ? expandedHeight : heightLimit}}
      >
        <div ref={contentRef} className="flow-root w-full">
          {children}
        </div>
      </CollapsibleContent>

      {isOverflowing ? (
        <CollapsibleTrigger asChild>{renderedTrigger}</CollapsibleTrigger>
      ) : null}
    </Collapsible>
  );
}
